import { NgIf, UpperCasePipe } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormGroup, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { SolicitacaoService } from 'src/app/core/apis/solicitacao/solicitacao.service';
import { Ordem } from 'src/app/core/model/ordem';
import { AuthService } from 'src/app/core/auth/service/auth.service';
import { Globals } from 'src/app/shared/helpers/globals';
import { DialogService } from 'src/app/core/service/dialog.service';
import { TipoOrdemPipe } from 'src/app/shared/pipe/tipo-ordem.pipe';
import { DateUtils } from 'src/app/shared/util/date-utils';
import { GenerateUrlCartaResponse } from '../../../core/model/generateUrlCartaResponse';
import { StatusOrdemPipe } from './../../../shared/pipe/status-ordem.pipe';
import { PageResourceListComponent } from 'src/app/shared/components/page-resource-list/page-resource-list.component';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { PageModel } from 'src/app/shared/components/page-resource-list/models/page.model';
import { PageableModel } from 'src/app/shared/components/page-resource-list/models/pageable.model';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    selector: 'app-listar-ordem',
    templateUrl: './listar-ordem.component.html',
    styleUrls: ['./listar-ordem.component.css'],
    providers: [provideNgxMask()],
    imports: [
        MatToolbarModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        NgxMaskDirective,
        MatButtonModule,
        MatIconModule,
        NgIf,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        UpperCasePipe,
        NgxMaskPipe,
        TipoOrdemPipe,
        StatusOrdemPipe,
        MatTooltip
    ]
})
export class ListarOrdemComponent extends PageResourceListComponent<Ordem> implements OnInit {
  INFORMATIVO = "'A consulta exibe as ordens cadastradas por <strong>todos os usuários da mesma unidade judiciária</strong>. Para verificar ordens de outras unidades, solicite consulta de histórico ou envie e-mail para suporte.serasajud@br.experian.com.'"

  @ViewChild(MatPaginator) paginatior: MatPaginator;

  form: FormGroup;
  tokenIam!: string;

  manterBusca: boolean = false;

  constructor(
    private solicitacaoService: SolicitacaoService,
    private authService: AuthService,
    private globals: Globals,
    private activatedRoute: ActivatedRoute,
    private dialogService: DialogService,
    private router: Router,
    protected injector: Injector,
    fb: UntypedFormBuilder
  ) {
    super(injector);
    this.form = fb.group({
      numeroProcesso: ['', [ Validators.minLength(20), Validators.maxLength(20) ]]
    });
  }

  ngOnInit() {
    this.dialogService.showAlert([this.INFORMATIVO], 'Entendi', '550px').subscribe(() => {
      this.globals.addToLoading('listar-ordem');

      this.tokenIam = this.authService.accessToken;
      if (!this.tokenIam)
        this.router.navigate(['login']);

      const nroProcessoParam = this.activatedRoute.snapshot.params['nroProcesso'];
      if (nroProcessoParam) {
        this.numeroProcesso.setValue(nroProcessoParam);
        this.manterBusca = true;
      }
      this.loadData(this.pageable);
    });
  }

  public buscarPorNroProcesso() {
    this.globals.addToLoading('listar-ordem');
    const nroProcesso = this.numeroProcesso.value;
    this.manterBusca = nroProcesso && nroProcesso.trim() !== "";
    this.loadData();
  }

  public limparBusca() {
    this.numeroProcesso.setValue("");
    this.loadData()
  }

  protected loadDataHandler(pageable: PageableModel): Observable<PageModel<Ordem>> {
    const numeroProcesso = this.numeroProcesso.value;
    this.manterBusca = numeroProcesso && numeroProcesso.trim() !== "";

    return this.solicitacaoService.consultarOrdensByOrgao(this.tokenIam, pageable, numeroProcesso).pipe(
      map(response => response.body),
      tap({
        next: (page) =>
          page.totalElements === 0 ? this.globals.showSnackBar("Nenhuma ordem encontrada para este usuário.") : null
        ,
        error: (error) => {
          this.globals.showSnackBar(error?.error?.Erros?.[0]?.message || "Falha ao consultar ordens, por favor tente novamente mais tarde.");
        },
        complete: () => this.globals.subtractFromLoading()
      }),
      catchError((error) => {
        return of(new PageModel<Ordem>());
      })
    );
  }

  protected loadDisplayedColumns(): string[] {
    return ['numeroProcesso', 'tipoOrdem','nroOrdem', 'nomeServidor', 'dtaAnotacao', 'status', 'cartaResposta', 'detalhes'];
  }

  public detalharOrdem(ordem: Ordem) {
    this.router.navigate(['detalhar-ordem', ordem.numeroProcesso], {
      state: { data: ordem, token: this.tokenIam, manterBusca: this.manterBusca }
    });
  }

  public formatData(data: string) {
    return DateUtils.formatDDMMYYYY(data);
  }

  public getCartaSolicitacao(ordem: Ordem) {
    this.globals.addToLoading('listar-ordem');
    this.solicitacaoService.getArquivoUrl(ordem.carta.idDtDrive, this.tokenIam).subscribe({
      next: (result: HttpResponse<GenerateUrlCartaResponse>) => {
        this.globals.subtractFromLoading();
        window.open(result.body.fileUrl,"_blank");
      },
      error: error => {
        console.error(error);
        this.globals.subtractFromLoading();
      }
    });
  }

  public setStyleByStatus(status: string): string {
    switch (status) {
      case 'CANCELADO':
        return 'color: red';
      case 'FINALIZADO':
        return 'color: green';
      default:
        return 'color: blue';
    }
  }

  get numeroProcesso() {
    return this.form.get('numeroProcesso');
  }

}
